import type {
  ClaimSeatResponse,
  CommitmentResponse,
  InvitePreviewResponse,
  RegistrationAttendeeResponse,
  RegistrationDiscoveryResponse,
} from '@@/types/api';
import { useApi } from './use-api';

export function useRegistrationApi() {
  const { get, post, patch, del } = useApi('/api/v1/events');

  return {
    // Discovery — what kind of registration does the user have for this event?
    getMyRegistration: (eventId: string) =>
      get<RegistrationDiscoveryResponse>(`/${eventId}/registration/mine`),

    // Single attendance row (claimed-attendee state)
    getMyAttendance: (eventId: string) =>
      get<RegistrationAttendeeResponse | null>(`/${eventId}/attendance/mine`),

    // Attendee rosters
    getCommitmentAttendees: (commitmentId: string) =>
      get<{ data: RegistrationAttendeeResponse[] }>(
        `/commitments/${commitmentId}/attendees`,
      ),

    getPurchaseAttendees: (purchaseId: string) =>
      get<{ data: RegistrationAttendeeResponse[] }>(
        `/purchases/${purchaseId}/attendees`,
      ),

    // Invite/rescind
    inviteToCommitment: (commitmentId: string, body: { email: string }) =>
      post<undefined>(`/commitments/${commitmentId}/attendees/invite`, {
        body,
      }),

    inviteToPurchase: (purchaseId: string, body: { email: string }) =>
      post<undefined>(`/purchases/${purchaseId}/attendees/invite`, { body }),

    rescindInvite: (attendeeId: string) =>
      del<{ message: string; attendee: RegistrationAttendeeResponse }>(
        `/attendees/${attendeeId}/invite`,
      ),

    // Claim flow (token-based; optional-auth — backend populates auth fields
    // when a Bearer token is present, returns auth: null otherwise)
    getInvitePreview: (token: string) =>
      get<InvitePreviewResponse>(`/attendees/invite/${token}`),

    claimSeat: (body: { token: string }) =>
      post<ClaimSeatResponse>('/attendees/claim', { body }),

    // Update party size on an existing commitment (optimistic locking via version).
    // Returns the CommitmentResponse UNWRAPPED — no `{ data }` envelope (#1980).
    updatePartySize: (
      eventId: string,
      commitmentId: string,
      body: { party_size: number; version: number },
    ) =>
      patch<CommitmentResponse>(
        `/${eventId}/commitments/${commitmentId}/party-size`,
        { body },
      ),
  };
}
